import { useEffect, useState } from "react";
import {
  fetchIntegrationGraph,
  EMPTY_INTEGRATION_GRAPH,
  IntegrationGraph,
} from "../services/IntegrationGraphService";

export type IntegrationGraphStatus = "loading" | "ready" | "error";

interface UseIntegrationGraphResult {
  graph: IntegrationGraph;
  status: IntegrationGraphStatus;
  error: string | null;
  /** Label for an empty relation cell, so a failed load never reads as "none". */
  emptyLabel: (whenEmpty: string) => string;
  /** Visual tone for that cell: a failed load is a warning, a real gap is not. */
  emptyVariant: "muted" | "warning";
  /** Same pair, for the connector column, which can fail on its own. */
  connectorEmptyLabel: (whenEmpty: string) => string;
  connectorEmptyVariant: "muted" | "warning";
}

/**
 * Loads the hotel / connector / SaaS graph for the internal listings.
 *
 * The status matters as much as the data: an empty relation cell means "nothing is
 * linked", but a failed query would render exactly the same way and quietly assert
 * something false. Callers use `emptyLabel` so the two stay distinguishable.
 */
export function useIntegrationGraph(): UseIntegrationGraphResult {
  const [graph, setGraph] = useState<IntegrationGraph>(EMPTY_INTEGRATION_GRAPH);
  const [status, setStatus] = useState<IntegrationGraphStatus>("loading");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const loaded = await fetchIntegrationGraph();
        if (cancelled) return;
        setGraph(loaded);
        setStatus("ready");
        setError(null);
      } catch (err: unknown) {
        if (cancelled) return;
        const message =
          err instanceof Error ? err.message : "Failed to load integration graph";
        setStatus("error");
        setError(message);
        console.error("Error loading integration graph:", err);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const emptyLabel = (whenEmpty: string): string => {
    if (status === "loading") return "Loading…";
    if (status === "error") return "Unavailable";
    return whenEmpty;
  };

  // The connector side reads a view that may not be deployed yet, while the SaaS
  // side comes from tables internal users can already read. They fail separately.
  const connectorsUnavailable = status === "ready" && !graph.connectorsAvailable;

  const connectorEmptyLabel = (whenEmpty: string): string => {
    if (status === "loading") return "Loading…";
    if (status === "error" || connectorsUnavailable) return "Unavailable";
    return whenEmpty;
  };

  return {
    graph,
    status,
    error,
    emptyLabel,
    emptyVariant: status === "error" ? "warning" : "muted",
    connectorEmptyLabel,
    connectorEmptyVariant:
      status === "error" || connectorsUnavailable ? "warning" : "muted",
  };
}
